import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";

/**
 * "What has this run produced that something could render?" — the index
 * behind the run view's visual panel (flowstack#950 Part B).
 *
 * Wire-format mirrors `RunVisualsResponse` in
 * `src/flowstack/server/routes/runs.py`.
 *
 * The endpoint is joined across the whole FLOW-RUN COHORT, which is the only
 * thing that makes it answerable for the run a user actually opens: dispatch
 * stamps each artifact with the CHILD run that produced it, so a parent flow
 * run owns none of its own. See `flowstack.server.run_cohort`.
 */
/**
 * Where an artifact came from, relative to this submission.
 *
 * `"input"` is the one that answers "which molecule is this run ABOUT": the
 * first `Molecule` an AFFDO run *produces* is a FRAGMENT of the ligand, so
 * picking the newest produced molecule would quietly put a fragment in the
 * panel and label it the run's subject.
 */
export type RunVisualOrigin = "input" | "produced";

export interface RunVisualArtifact {
  content_hash: string;
  typed_object: string;
  schema_version: string;
  origin: RunVisualOrigin;
  /** The CHILD run that produced it; `null` for an `input`. */
  run_id: string | null;
  /** Flow node that produced it; `null` for an input or a Tile run. */
  node_name: string | null;
  /**
   * Flow nodes that CONSUMED it, sorted (flowstack#956).
   *
   * What lets the panel answer "what did this step operate on" when a tile is
   * selected — at `fragment` the input is the parent ligand, at `torsion_fit`
   * a fragment. Filtering by this is still not a per-tile view map: the view
   * is picked from the TYPE afterwards, exactly as before.
   *
   * Optional so a fixture (or an API build predating #956) still typechecks;
   * absent is read as "no consumers known", never as "consumed by nothing".
   */
  consumed_by_nodes?: string[];
  created_at: string;
}

export interface RunVisualsResponse {
  run_id: string;
  flow_run_id: string | null;
  cohort_run_ids: string[];
  artifacts: RunVisualArtifact[];
}

/**
 * The views the visual panel can offer, and the typed objects that unlock each.
 *
 * **This is the whole of the view-selection logic, and it is keyed on TYPE.**
 * No tile name, no flow name, no node name appears here or anywhere downstream
 * — that is D70, and it is what lets a future `pharmaforge` / `refina` flow
 * light up the same panel with no wiring at all. A per-tile map would have to
 * grow an entry per producer and would silently show nothing for the first
 * flow nobody remembered to add.
 *
 * `unlockedBy` is a predicate over the artifact's `typed_object` rather than a
 * fixed set, so the molecule view inherits `STRUCTURE_BEARING_TYPES` (the
 * viewer's own registry) instead of re-listing it and drifting from it.
 */
export type RunVisualViewId = "molecule" | "torsion";

/**
 * Ordered so the DEFAULT view is first. The molecule leads because it is the
 * run's subject: the operator's ask is that it stays visible for the whole
 * live run, with the other views layering on as the flow produces them.
 */
export const RUN_VISUAL_VIEW_ORDER: readonly RunVisualViewId[] = ["molecule", "torsion"];

/** Typed objects whose presence means torsion profiles can be plotted. */
export const TORSION_VIEW_TYPES: ReadonlySet<string> = new Set(["TorsionRefinementResult"]);

export function runVisualsQueryKey(runId: string, progressKey: string): readonly unknown[] {
  return ["runs", "visuals", runId, progressKey] as const;
}

/**
 * The run's renderable-artifact index.
 *
 * **`progressKey` is the whole point.** This hook has NO `refetchInterval` —
 * introducing one would be a second polling loop over the same run, which is
 * exactly what flowstack#950 rules out. Instead the caller passes a key derived
 * from the run view's EXISTING progress mechanism (`useFlowRunDag`'s 5s poll,
 * `nextRefetchInterval` in `@/api/flow-run-dag`). When that poll observes a
 * node changing state, the key changes and this query re-reads; when the flow
 * is quiet — or terminal, when the DAG poll stops — this makes no requests at
 * all. Progressive updates therefore cost exactly the poll that was already
 * happening.
 *
 * `placeholderData` keeps the previous index visible across a key change, so a
 * node completing does not blank the molecule the user is watching — but ONLY
 * for the same run. React Query hands the callback the last data this observer
 * saw for ANY key, and `RunDetailPage` does not remount when the `run_id`
 * route param changes, so an unscoped placeholder would paint the previous
 * run's molecule under the new run's heading for a frame (Rupert PR-955 r0).
 * That is the same class of quietly-wrong-but-plausible render the
 * fragment-vs-ligand rule exists to prevent, so the previous query's key is
 * checked rather than trusted.
 *
 * `gcTime` is deliberately short. Each progress fingerprint mints a NEW cache
 * entry rather than refreshing one, so a long fanout run would otherwise
 * retain dozens of full indexes until the 5-minute default expired.
 */
export function useRunVisuals(runId: string, progressKey: string, options?: { enabled?: boolean }) {
  const enabled = (options?.enabled ?? true) && Boolean(runId);
  return useQuery({
    queryKey: runVisualsQueryKey(runId, progressKey),
    queryFn: async (): Promise<RunVisualsResponse> =>
      apiFetch<RunVisualsResponse>(`/api/runs/${encodeURIComponent(runId)}/visuals`),
    enabled,
    // Longer than the DAG's 5s poll on purpose: the key change is the
    // refresh signal, so a same-key remount (switching views, collapsing
    // the panel) should be served from cache rather than re-fetching.
    staleTime: 30_000,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === runId ? previous : undefined,
    gcTime: 60_000,
  });
}
